import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AlertTriangle, Check, Heart, X } from 'lucide-react';
import type { WellnessQuestion } from '../../../../../utils/types/wellness_types';
import type { IWellnessAssessmentModalProps } from '../../../interfaces';
import { getTypeLabel } from '../constants';
import { useWellnessAssessment } from '../hooks/useWellnessAssessment';

/**
 * MUI Dialog-based assessment modal.
 * All state is managed by useWellnessAssessment; this component is purely presentational.
 */
export const WellnessAssessmentModal = ({
  inviteId,
  isOpen,
  onClose,
  onProgress,
}: IWellnessAssessmentModalProps) => {
  const {
    state,
    current,
    nextUp,
    answers,
    submitting,
    allAnswered,
    label,
    options,
    answeredCount,
    totalQuestions,
    handleStart,
    handleSelect,
    handleSubmit,
    handleContinue,
  } = useWellnessAssessment(inviteId, isOpen, onProgress);

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* ── Close button ─────────────────────────────────────────────────── */}
      <IconButton
        onClick={onClose}
        aria-label="Close assessment"
        size="small"
        sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1, color: 'text.secondary' }}
      >
        <X size={20} />
      </IconButton>

      {/* ── Loading ──────────────────────────────────────────────────────── */}
      {state === 'loading' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, gap: 1.5 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Loading assessment…
          </Typography>
        </Box>
      )}

      {/* ── Error ────────────────────────────────────────────────────────── */}
      {state === 'error' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, gap: 1.5, px: 4, textAlign: 'center' }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'error.main',
            }}
          >
            <AlertTriangle size={24} />
          </Box>
          <Typography variant="h4">Something went wrong</Typography>
          <Typography variant="body2" color="text.secondary">
            Could not load this assessment. Please try again.
          </Typography>
        </Box>
      )}

      {/* ── Completed ────────────────────────────────────────────────────── */}
      {state === 'completed' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, gap: 1.5, px: 4, textAlign: 'center' }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'success.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'success.main',
            }}
          >
            <Check size={28} />
          </Box>
          <Typography variant="h3">All done</Typography>
          <Typography variant="body2" color="text.secondary">
            You have completed every screening for this invite. Thank you.
          </Typography>
          <Button variant="contained" onClick={onClose} sx={{ mt: 1 }}>
            Close
          </Button>
        </Box>
      )}

      {/* ── Intro ────────────────────────────────────────────────────────── */}
      {state === 'intro' && label && (
        <>
          <DialogContent sx={{ pt: 4, pb: 2 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main',
                }}
              >
                <Heart size={24} />
              </Box>

              <Box>
                <Typography variant="h2">{label.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {label.description}
                </Typography>
              </Box>

              <Box sx={{ bgcolor: 'grey.100', borderRadius: 2, p: 2 }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Before you begin
                </Typography>
                <Box
                  component="ul"
                  sx={{ pl: 2, m: 0, '& li': { typography: 'body2', color: 'text.secondary', mb: 0.5 } }}
                >
                  <li>There are {totalQuestions} short statements to respond to.</li>
                  <li>Answer honestly based on how you have felt recently — there are no right or wrong answers.</li>
                  <li>You will need to answer every question before you can submit.</li>
                  <li>Your responses are confidential.</li>
                </Box>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button variant="contained" onClick={handleStart} fullWidth>
              Start Assessment
            </Button>
          </DialogActions>
        </>
      )}

      {/* ── Questions ────────────────────────────────────────────────────── */}
      {state === 'questions' && current && label && (
        <>
          <DialogTitle sx={{ pb: 1, pr: 6 }}>
            <Typography variant="h3">{label.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {answeredCount} of {totalQuestions} answered
            </Typography>
          </DialogTitle>

          <DialogContent dividers sx={{ py: 3, overflowY: 'auto' }}>
            <Stack spacing={3}>
              {current.questions?.map((q: WellnessQuestion, index: number) => (
                <Box key={q.questionId}>
                  <Typography variant="body1" fontWeight={500} sx={{ mb: 1 }}>
                    {index + 1}. {q.question}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1,
                    }}
                  >
                    {options.map((optionLabel, value) => {
                      const isSelected = answers[q.questionId] === value;
                      return (
                        <Button
                          key={value}
                          variant="outlined"
                          onClick={() => handleSelect(q.questionId, value)}
                          sx={{
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            py: 1,
                            px: 1.5,
                            ...(isSelected && {
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                              borderColor: 'primary.main',
                              color: 'primary.dark',
                              fontWeight: 600,
                            }),
                          }}
                        >
                          {optionLabel}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </Button>
          </DialogActions>
        </>
      )}

      {/* ── Transition ───────────────────────────────────────────────────── */}
      {state === 'transition' && nextUp && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, gap: 1.5, px: 4, textAlign: 'center' }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'success.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'success.main',
            }}
          >
            <Check size={28} />
          </Box>
          <Typography variant="h3">Section complete</Typography>
          <Typography variant="body2" color="text.secondary">
            Up next: {getTypeLabel(nextUp.type ?? '').title}
          </Typography>
          <Button variant="contained" onClick={handleContinue} sx={{ mt: 1 }}>
            Continue
          </Button>
        </Box>
      )}
    </Dialog>
  );
};
